import { useCallback, useEffect, useState } from "react"

import { Box, ThemeProvider } from "@mui/material"
import { Biotech, FormatListNumbered } from "@mui/icons-material"

import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom"
import { GoogleOAuthProvider } from "@react-oauth/google"

import { LoginPage } from "../pages/LoginPage/LoginPage"
import { PredictionsListPage } from "../pages/PredictionsListPage/PredictionsListPage"
import { PredictionCreatePage } from "../pages/PredictionCreatePage/PredictionCreatePage"
import { PredictionPage } from "../pages/PredictionPage/PredictionPage"


import { NavBar } from "../components/NavBar/NavBar"
import { DrawerMenu } from "../components/DrawerMenu/DrawerMenu"
import { ThemeSelector } from "../components/ThemeSelector/ThemeSelector"
import { UserCard } from "../components/UserCard/UserCard"
import { MenuSection } from "../components/MenuSection/MenuSection"

import { ThemeModeProvider } from "../context/ThemeModeContext"
import { AuthProvider } from "../context/AuthProvider"

import { useThemeMode } from "../hooks/useThemeMode"
import { useAuth } from "../hooks/useAuth"

import { darkTheme, lightTheme } from "../config/theme"
import { strings } from "../config/strings"
import { refreshAuthentication } from "../utils/api"

const googleClientId = `${import.meta.env.VITE_GOOGLE_CLIENT_ID}`


export const Router = () => {
    return  (
        <GoogleOAuthProvider clientId={googleClientId}>
            <AuthProvider>
                <ThemeModeProvider>
                    <BrowserRouter>
                        <InnerRouter/>
                    </BrowserRouter>
                </ThemeModeProvider>
            </AuthProvider>
        </GoogleOAuthProvider>
    )
}

export const InnerRouter = () => {
    
    //State

    const [drawerOpen, setDrawerOpen] = useState<boolean>(false)


    //Hooks

    const themeMode = useThemeMode()
    const auth = useAuth()

    const navigate = useNavigate()


    //Callbacks

    const refreshAuth = useCallback( async () => { 
        try {
            await refreshAuthentication()
            // navigate('/')
        }
        catch (e) {
            console.log(e)
            navigate("/login")
        }
    }, [])

    //Effects

    useEffect(() => {
        if (!auth.user) {
            try {
                const user = auth.getCurrentUser()
                if (user) 
                    auth.setUser(user)
                else 
                    refreshAuth()
            } catch {
                refreshAuth()
            }
        }
    }, [])

    //Handlers

    const handleOpenDrawer = () => {
        setDrawerOpen(true)
    } 

    const handleCloseDrawer = () => {
        setDrawerOpen(false)
    } 

    const handleLogout = () => {
        setDrawerOpen(false)
        auth.logout()
    }


    //Render

    const user = auth.getCurrentUser()

    const renderNavBar = () => (
        user?(
                <NavBar 
                    initials={user.email[0]}
                    onLeftButtonClick={handleOpenDrawer}
                    onLogoutClick={ () => {
                            navigate('/login')
                            auth.logout()
                        }
                    }
                />
            ):
            undefined
    )


    const renderDrawerMenu = () => {

            return (user)?(
            <DrawerMenu
                sx={{height:'calc(100vh - 64px)'}}
                open={drawerOpen}
                onClose={(handleCloseDrawer)}
                footer={
                    <ThemeSelector />
                }>
                <Box>
                    <UserCard 
                        username={user.email??''} 
                        userRole='Oppenheimer User' 
                        onLogoutClick={handleLogout} 
                    />
                    <MenuSection 
                        title={strings.pmpp}
                        menuItems= {[
                            {title: `${strings.prediction_jobs}`, icon:<Biotech/>, link: '/',},
                            {title: `${strings.new_prediction}`, icon:<FormatListNumbered/>, link: '/new',},
                        ]}
                        onItemClick={handleCloseDrawer}
                    />
                </Box>
            </DrawerMenu>
        ):''
    }

    return( 
        <ThemeProvider theme={themeMode.getCurrentMode()==='light'?lightTheme:darkTheme}>
            <Box display='flex' flexDirection='column' height='100vh' overflow='hidden'>
                {renderNavBar()}
                {renderDrawerMenu()}
                <Box height='100%' overflow='scroll'>
                    <Routes>
                        <Route path="/login" element={<LoginPage/>}/>
                        <Route path="" element={
                            <PredictionsListPage/>
                        }/>
                        <Route path="/new" element={<PredictionCreatePage/>}/>
                        <Route path="/predictions/:job_id" element={<PredictionPage/>}/>
                    </Routes>
                </Box>
            </Box>
        </ThemeProvider>
    )
}